import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ 
  user = null, 
  isCollapsed = false, 
  onToggleCollapse = () => {},
  onLogout = () => {} 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isAdminUser = user?.role === 'admin';

  const customerNavItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard', tooltip: 'Account Overview' },
    { label: 'Send Money', path: '/send-money-flow', icon: 'Send', tooltip: 'Transfer Funds' },
    { label: 'Recipients', path: '/recipients-management', icon: 'Users', tooltip: 'Manage Recipients' },
    { label: 'Transactions', path: '/transaction-history', icon: 'History', tooltip: 'Transaction History' },
    { label: 'Profile', path: '/profile-management', icon: 'User', tooltip: 'Profile Settings' },
  ];

  const adminNavItems = [
    { label: 'Admin Dashboard', path: '/admin-dashboard', icon: 'Shield', tooltip: 'Admin Overview' },
    { label: 'Transaction Review', path: '/transaction-review', icon: 'FileCheck', tooltip: 'Review Queue' },
    { label: 'Document Upload', path: '/document-upload', icon: 'Upload', tooltip: 'Document Management' },
  ];

  const navigationItems = isAdminUser ? adminNavItems : customerNavItems;

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const Logo = () => (
    <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-4 py-6`}>
      <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon name="ArrowRightLeft" size={20} color="white" />
      </div>
      {!isCollapsed && (
        <div className="flex flex-col">
          <span className="text-lg font-semibold text-foreground">MoneyTransfer</span>
          <span className="text-sm text-muted-foreground">Pro</span>
        </div>
      )}
    </div>
  );

  return (
    <div className={`bg-card border-r border-border h-screen lg:fixed lg:inset-y-0 lg:left-0 lg:z-navigation flex flex-col transition-all duration-300 ${
      isCollapsed ? 'w-16' : 'w-64'
    }`}>
      {/* Logo Section */}
      <div className="border-b border-border">
        <Logo />
      </div>
      {/* Navigation Items */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {navigationItems?.map((item) => {
          const isActive = location?.pathname === item?.path;
          
          return (
            <div key={item?.path} className="relative group">
              <button
                onClick={() => handleNavigation(item?.path)}
                className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 hover:scale-102 ${
                  isActive
                    ? 'text-primary bg-primary/10 border-l-2 border-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                } ${isCollapsed ? 'justify-center' : 'space-x-3'}`}
              >
                <Icon name={item?.icon} size={20} className="flex-shrink-0" />
                {!isCollapsed && <span>{item?.label}</span>}
              </button>
              {/* Tooltip for collapsed state */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-tooltip opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-mobile-overlay">
                  {item?.tooltip || item?.label}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      {/* User Section */}
      <div className="border-t border-border p-2">
        {/* User Profile */}
        <div className="relative group">
          <button
            onClick={() => navigate('/profile-management')}
            className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 ${
              isCollapsed ? 'justify-center' : 'space-x-3'
            }`}
          >
            <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={16} color="white" />
            </div>
            {!isCollapsed && (
              <div className="flex flex-col items-start min-w-0">
                <span className="text-sm font-medium truncate">{user?.name || 'User'}</span>
                <span className="text-xs text-muted-foreground capitalize">{user?.role || 'Customer'}</span>
              </div>
            )}
          </button>
          
          {isCollapsed && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-tooltip opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-mobile-overlay">
              {user?.name || 'Profile'}
            </div>
          )}
        </div>

        {/* Collapse Toggle */}
        <div className="mt-2 relative group">
          <Button
            variant="ghost"
            onClick={onToggleCollapse}
            className={`w-full ${isCollapsed ? 'justify-center' : 'justify-start space-x-3'}`}
            iconName={isCollapsed ? "ChevronRight" : "ChevronLeft"}
            iconPosition={isCollapsed ? undefined : "left"}
            iconSize={16}
          >
            {!isCollapsed && 'Collapse'}
          </Button>
          
          {isCollapsed && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-tooltip opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-mobile-overlay">
              Expand Sidebar
            </div>
          )}
        </div>

        {/* Logout */}
        <div className="mt-2 relative group">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={`w-full text-muted-foreground hover:text-foreground ${
              isCollapsed ? 'justify-center' : 'justify-start space-x-3'
            }`}
            iconName="LogOut"
            iconPosition={isCollapsed ? undefined : "left"}
            iconSize={16}
          >
            {!isCollapsed && 'Logout'}
          </Button>
          
          {isCollapsed && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-tooltip opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-mobile-overlay">
              Logout
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;